import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiActivity, FiArrowRight } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import GlowText from '../components/ui/GlowText';
import ChatbotModal from '../components/chatbots/ChatbotModal';

const chatbots = [
  {
    id: 1,
    title: 'PROUT Research Assistant',
    description: 'Explore PROUT as articulated by P. R. Sarkar, and test its principles against real-world locations, economies, and social conditions, with structured, evidence-aware analysis.',
    icon: FiActivity,
    color: 'cyan',
    status: 'Beta',
    launchable: true,
    iframeUrl: "https://studio.ai-pro.dk/_embed/NY42Y3776FH?d=deployment-20d7ee5a-7bb4-4d9f-aef4-77a55dd17eb3"
  },
  {
    id: 2,
    title: 'War Signal Clarity',
    description: 'A calm geopolitical explainer that helps distinguish routine military activity, deterrent signaling, active escalation, and genuine warning signs — without panic or sensationalism.',
    icon: FiCpu,
    color: 'gold',
    status: 'BETA',
    launchable: true,
    iframeUrl: "https://studio.pickaxe.co/_embed/HTXIFA4O5K?d=deployment-2cc2bee4-5f15-46f7-835b-13bb1ae35ec0"
  }
];

const ChatbotCard = ({ bot, onLaunch }) => {
  const Icon = bot.icon;
  const colorClasses = {
    cyan: 'text-cyan-400 group-hover:shadow-cyan-500/50',
    purple: 'text-purple-400 group-hover:shadow-purple-500/50',
    gold: 'text-amber-400 group-hover:shadow-amber-500/50',
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="group relative bg-dark-700/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-dark-700 transition-all duration-500 flex flex-col h-full"
    >
      <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center mb-6 border border-white/5 group-hover:border-white/20 transition-colors">
        <Icon className={`text-2xl ${colorClasses[bot.color].split(' ')[0]}`} />
      </div>
      
      <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-white transition-colors">
        {bot.title}
      </h3>
      
      <p className="text-gray-400 mb-8 leading-relaxed">
        {bot.description}
      </p>
      
      <div className="flex items-center justify-between mt-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-gray-500 border border-white/10 px-2 py-1 rounded">
          {bot.status}
        </span>
        {bot.launchable && (
          <button 
            onClick={() => onLaunch(bot)}
            className="flex items-center space-x-2 text-sm font-medium text-white hover:text-cyan-400 transition-colors group/btn"
          >
            <span>Launch</span>
            <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
          </button>
        )}
      </div>

      <div className={`absolute inset-0 rounded-2xl border-2 border-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none ${bot.color === 'cyan' ? 'border-cyan-500/20' : bot.color === 'purple' ? 'border-purple-500/20' : 'border-amber-500/20'}`} />
    </motion.div>
  );
};

const Chatbots = () => {
  const [activeBot, setActiveBot] = useState(null);

  const handleLaunch = (bot) => {
    setActiveBot(bot);
  };

  const closeChat = () => {
    setActiveBot(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-900">
      <Navbar />
      
      <main className="flex-grow pt-32 pb-20 px-6 relative">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-900/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <GlowText as="h1" className="text-4xl md:text-6xl text-white mb-6">
              Intelligent Assistants
            </GlowText>
            <p className="text-xl text-gray-400 max-w-2xl font-light">
              Specialised AI models designed for clarity, depth, and practical insight. Interact with curated knowledge bases.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
            {chatbots.map(bot => (
              <ChatbotCard key={bot.id} bot={bot} onLaunch={handleLaunch} />
            ))}
          </div>
        </div>
      </main>

      {/* Chatbot Modal */}
      <ChatbotModal 
        isOpen={!!activeBot} 
        onClose={closeChat} 
        iframeUrl={activeBot?.iframeUrl} 
      />

      <Footer />
    </div>
  );
};

export default Chatbots;